import {Injectable} from "@angular/core";
import {ServiceState} from "../stores/service.store";
import {DrawerViewModel, LocationViewModel, ServiceViewModel, WorkerCardConfig, WorkerViewModel} from "../view-model/service.view-model";

@Injectable({ providedIn: 'root' })
export class ServiceMapper {
  public toVM(state: ServiceState, workerCardConfig: WorkerCardConfig): ServiceViewModel {
    const selection = state.selection ?? {
      location: null,
      drawer: null,
      worker: null,
    };
    const isWorkersOwnedDevice = state.isWorkersOwnedDevice;
    const relations = state.serviceRelations;

    const filteredLocations = this.filterLocations(state.locations, selection, isWorkersOwnedDevice, relations);
    const filteredDrawers = this.filterDrawers(state.drawers, selection, relations);
    const filteredWorkers = this.filterWorkers(state.workers, selection, isWorkersOwnedDevice, relations);

    return {
      locations: this.mapLocationsWithSelection(filteredLocations, selection),
      drawers: this.mapDrawersWithSelection(filteredDrawers, selection),
      workers: this.mapWorkersWithSelection(filteredWorkers, selection),
      selection,
      shouldShowLocations: this.shouldShowLocations(selection, isWorkersOwnedDevice),
      shouldShowDrawers: this.shouldShowDrawers(selection, isWorkersOwnedDevice),
      shouldShowWorkers: this.shouldShowWorkers(selection, isWorkersOwnedDevice),
      workerCardConfig,
    };
  }

  private filterLocations(
    allLocations: ServiceState['locations'],
    selection: ServiceState['selection'],
    isWorkersOwnedDevice: boolean,
    relations: ServiceState['serviceRelations']
  ): ServiceState['locations'] {
    if (!relations) {
      return [];
    }

    if (!isWorkersOwnedDevice) {
      const locationIdsWithDrawers = Object.keys(relations.locationsToCashDrawers);

      return allLocations.filter((location) => locationIdsWithDrawers.includes(location.id));
    }

    if (!selection?.worker) {
      return [];
    }

    const locationIds = relations.workersToLocations[selection.worker.id] || [];

    return allLocations.filter((location) => locationIds.includes(location.id));
  }

  private filterDrawers(
    allDrawers: ServiceState['drawers'],
    selection: ServiceState['selection'],
    relations: ServiceState['serviceRelations']
  ): ServiceState['drawers'] {
    if (!selection?.location) {
      return [];
    }

    if (!relations) {
      return [];
    }

    const drawerIds = relations.locationsToCashDrawers[selection.location.id] || [];

    return allDrawers.filter((drawer) => drawerIds.includes(drawer.id));
  }

  private filterWorkers(
    allWorkers: ServiceState['workers'],
    selection: ServiceState['selection'],
    isWorkersOwnedDevice: boolean,
    relations: ServiceState['serviceRelations']
  ): ServiceState['workers'] {
    // Non-personal device: filter by cashDrawersToWorkers when drawer is selected
    if (!isWorkersOwnedDevice) {
      if (!selection?.drawer || !relations) {
        return [];
      }

      const workerIds = relations.cashDrawersToWorkers[selection.drawer.id] || [];

      return allWorkers.filter((worker) => workerIds.includes(worker.id));
    }

    // Personal device: show all workers initially, then filter by cashDrawersToWorkers when drawer is selected
    if (!selection?.drawer) {
      return allWorkers;
    }

    if (!relations) {
      return [];
    }

    const workerIds = relations.cashDrawersToWorkers[selection.drawer.id] || [];

    return allWorkers.filter((worker) => workerIds.includes(worker.id));
  }

  private shouldShowLocations(
    selection: ServiceState['selection'],
    isWorkersOwnedDevice: boolean
  ): boolean {
    if (isWorkersOwnedDevice) {
      // Personal device: worker → location → drawer
      // Show locations when worker is selected and drawer is not selected
      return !!(selection?.worker && !selection?.drawer);
    } else {
      // Non-personal device: location → drawer → worker
      // Show locations when drawer is not selected
      return !selection?.drawer;
    }
  }

  private shouldShowDrawers(
    selection: ServiceState['selection'],
    isWorkersOwnedDevice: boolean
  ): boolean {
    if (isWorkersOwnedDevice) {
      // Personal device: worker → location → drawer
      // Show drawers when both worker and location are selected but drawer is not
      return !!(selection?.worker && selection?.location && !selection?.drawer);
    } else {
      // Non-personal device: location → drawer → worker
      // Show drawers when location is selected but drawer is not
      return !!(selection?.location && !selection?.drawer);
    }
  }

  private shouldShowWorkers(
    selection: ServiceState['selection'],
    isWorkersOwnedDevice: boolean
  ): boolean {
    if (isWorkersOwnedDevice) {
      // Personal device: worker → location → drawer
      // Show workers when nothing is selected yet
      return !selection?.worker && !selection?.location && !selection?.drawer;
    } else {
      // Non-personal device: location → drawer → worker
      // Show workers when both location and drawer are selected
      return !!(selection?.location && selection?.drawer);
    }
  }

  private mapLocationsWithSelection(
    locations: ServiceState['locations'],
    selection: ServiceState['selection']
  ): LocationViewModel[] {
    const selectedLocationId = selection?.location?.id;

    return locations.map((location) => ({
      ...location,
      isSelected: selectedLocationId ? location.id === selectedLocationId : true,
    }));
  }

  private mapDrawersWithSelection(
    drawers: ServiceState['drawers'],
    selection: ServiceState['selection']
  ): DrawerViewModel[] {
    const selectedDrawerId = selection?.drawer?.id;

    return drawers.map((drawer) => ({
      ...drawer,
      isSelected: selectedDrawerId ? drawer.id === selectedDrawerId : true,
    }));
  }

  private mapWorkersWithSelection(
    workers: ServiceState['workers'],
    selection: ServiceState['selection']
  ): WorkerViewModel[] {
    const selectedWorkerId = selection?.worker?.id;

    return workers.map((worker) => ({
      ...worker,
      isSelected: selectedWorkerId ? worker.id === selectedWorkerId : true,
    }));
  }
}
